import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import AuthService from "../../modules/Admin/services/AuthService";
import NotificationService from "../../modules/Admin/services/NotificationService";
import LeadService from "../../modules/Admin/services/LeadService";
import LeadsAssignedService from "../../modules/Admin/services/LeadsAssignedService";
import profilePic from "../../Images/DreamTown.png";
import { Badge } from "primereact/badge";
import axios from "axios";
import {
  FaBars,
  FaBuilding,
  FaSearch,
  FaSignOutAlt,
  FaTrash,
  FaUser,
  FaUserAlt,
} from "react-icons/fa";

function Navbar() {
  const [isLoggedIn, setIsLoggedIn] = useState(AuthService.isAuthenticated());
  const [searchTerm, setSearchTerm] = useState("");
  const [showNotifications, setShowNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const toast = useRef(null);
  const [currentTime, setCurrentTime] = useState("");
  const [greeting, setGreeting] = useState("");
  const [userId, setUserId] = useState(AuthService.getLoggedInUserId());
  const [lastLoginTime, setLastLoginTime] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [assignedNotifications, setAssignedNotifications] = useState([]);
  const [overdueCount, setOverdueCount] = useState(0);
  const userRole = AuthService.getLoggedInRoleId();
  const [userImage, setUserImage] = useState("");

  useEffect(() => {
    const jwtUserId = AuthService.getLoggedInUserId();
    if (jwtUserId) {
      setUserId(jwtUserId);
    } else {
      console.error("User is not logged in.");
      navigate("/login");
    }
  }, []);

  useEffect(() => {
    async function fetchNotificationsByDate() {
      try {
        const response =
          await NotificationService.getNotificationsByDateAsync(userId);
        setAssignedNotifications(response);
        console.log(response);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setLoading(false);
      }
    }
    fetchNotificationsByDate();
  }, [userId]);

  useEffect(() => {
    async function getUserImageById() {
      try {
        const response = await AuthService.getUserImageById(userId);
        setUserImage(response);
        console.log(response);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setLoading(false);
      }
    }
    getUserImageById();
  }, [userId]);

  useEffect(() => {
    async function getNotifications() {
      try {
        const response =
          await NotificationService.getNotificationsByDate(userId);
        setAssignedNotifications(response);
        setTotalCount(response.totalCount || 0);
        setAssignedNotifications(response.leadAssignedNotifications);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching notifications:", error);
        setLoading(false);
      }
    }
    getNotifications();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const clientId = AuthService.getLoggedInClientId();
    if (!clientId) {
      setOverdueCount(0);
      return;
    }
    LeadsAssignedService.getPendingFollowupCount(userId, clientId)
      .then((count) => setOverdueCount(count ?? 0))
      .catch(() => {});
  }, [userId]);

  useEffect(() => {
    const updateTimeAndGreeting = () => {
      const date = new Date();
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const ampm = hours >= 12 ? "PM" : "AM";

      if (hours < 12) {
        setGreeting("Good Morning");
      } else if (hours < 18) {
        setGreeting("Good Afternoon");
      } else {
        setGreeting("Good Evening");
      }

      hours = hours % 12;
      hours = hours ? hours : 12;
      const minutesFormatted = minutes < 10 ? "0" + minutes : minutes;
      const secondsFormatted = seconds < 10 ? "0" + seconds : seconds;

      setCurrentTime(
        `${hours}:${minutesFormatted}:${secondsFormatted} ${ampm}`,
      );
    };

    updateTimeAndGreeting();
    const intervalId = setInterval(updateTimeAndGreeting, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const fetchLastLoginTime = async () => {
      try {
        const lastLogin = await AuthService.getLastLoginTime(userId);
        if (lastLogin) {
          const formattedTime = new Date(lastLogin).toLocaleString();
          setLastLoginTime(formattedTime);
        }
      } catch (error) {
        console.error("Error fetching last login time:", error);
      }
    };

    if (userId) {
      fetchLastLoginTime();
    }
  }, [userId]);

  const Role = {
    SUPERADMIN: { roleId: 1, roleName: "SuperAdmin" },
    ADMIN: { roleId: 2, roleName: "Admin" },
    EMPLOYEE: { roleId: 4, roleName: "Employee" },
  };

  const handleLogout = () => {
    if (toast.current) {
      toast.current.show({
        severity: "success",
        summary: "Logged Out",
        detail: "User Logged Out successfully!!",
        className: "custom-toast-delete",
      });
    }
    setIsLoggedIn(false);
    setTimeout(() => {
      AuthService.performLogout(navigate);
    }, 700);
  };

  const handleAccountClick = () => {
    const userId = AuthService.getLoggedInUserId();
    navigate(`/edituser/${userId}`);
  };

  function handleNotificationClick() {
    setShowNotifications(!showNotifications);
  }

  const toggleSidebar = () => {
    document.body.classList.toggle("sidebar-open");
  };

  const handleSearch = async () => {
    if (searchTerm.trim() !== "") {
      try {
        const leads = await LeadService.searchLeads(searchTerm);
        navigate(`/search?query=${encodeURIComponent(searchTerm)}`, {
          state: { leads },
        });
      } catch (error) {
        console.error("Error performing search:", error);
        toast.current.show({
          severity: "error",
          summary: "Search Error",
          detail: "An error occurred while searching. Please try again later.",
          life: 3000,
        });
      }
    }
    setSearchTerm("");
  };

  const refreshNotifications = async () => {
    setLoading(true); // Show loader while fetching
    try {
      const response = await NotificationService.getNotificationsByDate(userId);
      setAssignedNotifications(response.leadAssignedNotifications);
      setTotalCount(response.totalCount || 0);
    } catch (error) {
      console.error("Error refreshing notifications:", error);
    } finally {
      setLoading(false); // Hide loader once fetching is done
    }
  };

  function formatTimeElapsed(timestamp) {
    const timeElapsed = Math.floor((new Date() - new Date(timestamp)) / 1000);
    if (timeElapsed < 60) {
      return `${timeElapsed} seconds ago`;
    } else if (timeElapsed < 3600) {
      return `${Math.floor(timeElapsed / 60)} minutes ago`;
    } else if (timeElapsed < 86400) {
      return `${Math.floor(timeElapsed / 3600)} hours ago`;
    } else {
      return `${Math.floor(timeElapsed / 86400)} days ago`;
    }
  }

  const roleNum = parseInt(userRole, 10);
  const isAdminRole =
    roleNum === Role.SUPERADMIN.roleId ||
    roleNum === Role.ADMIN.roleId ||
    roleNum === 3; // legacy JWT “Manager”; shown as Admin in UI

  const roleName =
    roleNum === Role.SUPERADMIN.roleId
      ? "Super Admin"
      : roleNum === Role.ADMIN.roleId || roleNum === 3
        ? "Admin"
        : "Employee";

  const hasValidUserImage =
    typeof userImage === "string" &&
    userImage.length > 0 &&
    userImage !== "undefined" &&
    userImage !== "null";

  return (
    <nav
      style={{
        background: "#fff",
        borderBottom: "1px solid #f0f2f5",
        padding: "0 20px",
        height: "64px",
        display: "flex",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 999,
        boxShadow: "0 2px 12px rgba(52,71,103,0.07)",
      }}
      id="navbarBlur"
    >
      <Toast ref={toast} />
      <style>{`
        .nb-iconbtn{background:none;border:none;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center}
        .nb-iconbtn:focus{outline:none}
        .nb-notif-item{padding:10px 14px;border-radius:10px;cursor:default;transition:background .15s}
        .nb-notif-item:hover{background:#f8f9fe}
        .nb-dditem{display:flex;align-items:center;gap:10px;padding:10px 14px;border-radius:10px;background:none;border:none;width:100%;font-size:13px;font-weight:600;cursor:pointer;transition:background .15s;color:#344767}
        .nb-dditem:hover{background:#f8f9fe}
        .nb-dditem.danger:hover{background:#fff0f0;color:#f5365c}
        .nb-searchinp:focus{border-color:#344767!important;box-shadow:0 0 0 3px rgba(52,71,103,0.1)!important;outline:none}
        .nb-togglebtn:hover{background:#f4f6fb!important}
      `}</style>

      {/* ── Left: hamburger + greeting ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "14px", flex: "0 0 auto" }}>
        <button
          type="button"
          className="nb-iconbtn nb-togglebtn"
          onClick={toggleSidebar}
          aria-label="Toggle menu"
          style={{ width: "38px", height: "38px", borderRadius: "10px", background: "transparent", transition: "background .15s", color: "#344767", fontSize: "16px" }}
        >
          <FaBars />
        </button>
        <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
          <span style={{ fontSize: "11px", color: "#8392ab", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.04em" }}>{greeting}</span>
          <span style={{ fontSize: "13px", color: "#344767", fontWeight: "700" }}>{currentTime}</span>
        </div>
      </div>

      {/* ── Centre: search ── */}
      <div style={{ flex: 1, display: "flex", justifyContent: "center", padding: "0 24px" }}>
        <div style={{ position: "relative", width: "100%", maxWidth: "380px" }}>
          <FaSearch style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "#8392ab", fontSize: "13px", pointerEvents: "none" }} />
          <input
            className="nb-searchinp"
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => { if (e.key === "Enter") { handleSearch(); setSearchTerm(""); } }}
            placeholder="Search leads…"
            style={{ width: "100%", padding: "9px 14px 9px 36px", border: "1.5px solid #e9ecef", borderRadius: "10px", fontSize: "13px", color: "#344767", background: "#f8f9fe", transition: "border-color .2s,box-shadow .2s" }}
          />
        </div>
      </div>

      {/* ── Right: notification + profile ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", flex: "0 0 auto" }}>

        {/* Bell */}
        <div className="nav-item dropdown" style={{ position: "relative" }}>
          <button
            className="nb-iconbtn"
            id="notifDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{ width: "40px", height: "40px", borderRadius: "12px", background: "#f8f9fe", border: "1.5px solid #e9ecef", position: "relative", color: "#344767", fontSize: "16px", transition: "background .15s" }}
          >
            <i className="fas fa-bell" />
            {(totalCount + overdueCount) > 0 && (
              <span style={{ position: "absolute", top: "6px", right: "6px", width: "8px", height: "8px", borderRadius: "50%", background: "#f5365c", border: "2px solid #fff" }} />
            )}
          </button>
          <div
            className="dropdown-menu dropdown-menu-end"
            aria-labelledby="notifDropdown"
            style={{ minWidth: "320px", border: "none", borderRadius: "16px", boxShadow: "0 10px 40px rgba(52,71,103,0.15)", padding: "0", overflow: "hidden", marginTop: "8px" }}
          >
            {/* Header */}
            <div style={{ padding: "16px 18px 12px", borderBottom: "1px solid #f0f2f5", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <p style={{ margin: 0, fontWeight: "700", color: "#344767", fontSize: "14px" }}>Notifications</p>
                <p style={{ margin: 0, fontSize: "11px", color: "#8392ab" }}>Today's alerts</p>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                {(totalCount + overdueCount) > 0 && (
                  <span style={{ background: "#f0f2ff", color: "#5e72e4", borderRadius: "20px", padding: "3px 10px", fontSize: "11px", fontWeight: "700" }}>
                    {totalCount + overdueCount} new
                  </span>
                )}
                <button
                  className="nb-iconbtn"
                  onClick={refreshNotifications}
                  title="Refresh"
                  style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#f4f6fb", color: "#8392ab", fontSize: "12px" }}
                >
                  <i className="fas fa-sync-alt" />
                </button>
              </div>
            </div>
            {/* Body */}
            <div style={{ maxHeight: "360px", overflowY: "auto", padding: "8px" }}>
              {loading ? (
                <div style={{ padding: "24px", textAlign: "center", color: "#8392ab", fontSize: "13px" }}>
                  <i className="fas fa-circle-notch fa-spin" style={{ marginRight: "8px" }} />Loading…
                </div>
              ) : (
                <>
                  {/* Overdue Follow-Ups alert */}
                  {overdueCount > 0 && (
                    <div
                      className="nb-notif-item"
                      style={{ cursor: "pointer" }}
                      onClick={() => navigate("/pendingfollowups")}
                    >
                      <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                        <div style={{ width: "32px", height: "32px", borderRadius: "10px", background: "#f5365c18", display: "flex", alignItems: "center", justifyContent: "center", color: "#f5365c", fontSize: "13px", flexShrink: 0 }}>
                          <i className="fas fa-exclamation-circle" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: 0, fontSize: "12px", color: "#f5365c", fontWeight: "700", lineHeight: 1.4 }}>
                            {overdueCount} Follow-Up{overdueCount > 1 ? "s" : ""} Overdue
                          </p>
                          <p style={{ margin: "3px 0 0", fontSize: "11px", color: "#8392ab" }}>
                            <i className="fas fa-clock" style={{ marginRight: "4px" }} />
                            Tap to view pending follow-ups
                          </p>
                        </div>
                        <span style={{ background: "#f5365c", color: "#fff", borderRadius: "12px", padding: "2px 8px", fontSize: "10px", fontWeight: "700", flexShrink: 0, alignSelf: "center" }}>
                          {overdueCount}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Assignment notifications */}
                  {assignedNotifications && assignedNotifications.length > 0 ? (
                    assignedNotifications.map((n, i) => (
                      <div key={n.date || i} className="nb-notif-item">
                        <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                          <div style={{ width: "32px", height: "32px", borderRadius: "10px", background: "#f0f2ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#5e72e4", fontSize: "13px", flexShrink: 0 }}>
                            <i className="fas fa-user-check" />
                          </div>
                          <div style={{ flex: 1 }}>
                            <p style={{ margin: 0, fontSize: "12px", color: "#344767", fontWeight: "600", lineHeight: 1.4 }}>{n.message}</p>
                            <p style={{ margin: "3px 0 0", fontSize: "11px", color: "#8392ab" }}>
                              <i className="fas fa-clock" style={{ marginRight: "4px" }} />
                              {formatTimeElapsed(n.date)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : overdueCount === 0 ? (
                    <div style={{ padding: "28px 16px", textAlign: "center" }}>
                      <div style={{ width: "44px", height: "44px", borderRadius: "50%", background: "#f4f6fb", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 10px", fontSize: "18px", color: "#c0c8d8" }}>
                        <i className="fas fa-bell-slash" />
                      </div>
                      <p style={{ color: "#8898aa", fontSize: "12px", fontWeight: "600", margin: 0 }}>No new notifications</p>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: "1px", height: "28px", background: "#e9ecef", margin: "0 4px" }} />

        {/* Profile */}
        <div className="nav-item dropdown" style={{ position: "relative" }}>
          <button
            className="nb-iconbtn"
            id="profileDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            style={{ display: "flex", alignItems: "center", gap: "10px", padding: "4px 10px 4px 4px", borderRadius: "12px", background: "#f8f9fe", border: "1.5px solid #e9ecef", cursor: "pointer" }}
          >
            <div style={{ width: "34px", height: "34px", borderRadius: "10px", overflow: "hidden", flexShrink: 0, border: "2px solid #fff", boxShadow: "0 2px 6px rgba(52,71,103,0.15)" }}>
              {isAdminRole ? (
                <img src={profilePic} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                hasValidUserImage
                  ? <img src={`data:image/jpeg;base64,${userImage}`} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  : <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#344767,#5e72e4)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "14px", fontWeight: "700" }}><FaUser /></div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: 1.2 }}>
              <span style={{ fontSize: "12px", fontWeight: "700", color: "#344767" }}>My Account</span>
              <span style={{ fontSize: "10px", color: "#8392ab", fontWeight: "600" }}>{roleName}</span>
            </div>
            <i className="fas fa-chevron-down" style={{ fontSize: "10px", color: "#8392ab", marginLeft: "2px" }} />
          </button>

          <div
            className="dropdown-menu dropdown-menu-end"
            aria-labelledby="profileDropdown"
            style={{ minWidth: "200px", border: "none", borderRadius: "16px", boxShadow: "0 10px 40px rgba(52,71,103,0.15)", padding: "8px", marginTop: "8px" }}
          >
            {/* Profile header */}
            <div style={{ padding: "10px 14px 12px", borderBottom: "1px solid #f0f2f5", marginBottom: "6px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{ width: "38px", height: "38px", borderRadius: "10px", overflow: "hidden", border: "2px solid #e9ecef" }}>
                  {isAdminRole ? (
                    <img src={profilePic} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  ) : (
                    hasValidUserImage
                      ? <img src={`data:image/jpeg;base64,${userImage}`} alt="Profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      : <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#344767,#5e72e4)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "14px" }}><FaUser /></div>
                  )}
                </div>
                <div>
                  <p style={{ margin: 0, fontWeight: "700", color: "#344767", fontSize: "13px" }}>Account</p>
                  <span style={{ fontSize: "11px", color: "#8392ab" }}>{roleName}</span>
                </div>
              </div>
            </div>

            <button className="nb-dditem" onClick={handleAccountClick}>
              <div style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#f0f2ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#5e72e4", fontSize: "12px" }}>
                <FaUser />
              </div>
              Your Profile
            </button>

            <div style={{ height: "1px", background: "#f0f2f5", margin: "6px 0" }} />

            {isLoggedIn ? (
              <button className="nb-dditem danger" onClick={handleLogout}>
                <div style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#fff0f0", display: "flex", alignItems: "center", justifyContent: "center", color: "#f5365c", fontSize: "12px" }}>
                  <FaSignOutAlt />
                </div>
                Log Out
              </button>
            ) : (
              <button className="nb-dditem" onClick={() => navigate("/login")}>
                <div style={{ width: "28px", height: "28px", borderRadius: "8px", background: "#f0f2ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#5e72e4", fontSize: "12px" }}>
                  <FaUserAlt />
                </div>
                Login
              </button>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
